import { API_ORIGIN } from "@/lib/apiOrigin";
import { useRef, useState } from "react";
import { Link } from "react-router-dom";
import {
  MapPin, Package, Clock, ArrowRight, Loader2, Sparkles,
  Truck, Box,
} from "lucide-react";
import axios from "axios";
import AddressAutocompleteInput from "./AddressAutocompleteInput";
import CrossBorderNotice from "./CrossBorderNotice";
import { DELIVERY_TYPE_ICON } from "@/lib/delivery_icons";
import { selectDeliveryService, selectParcelCategory } from "@/lib/parcelSelection";
import { formatApiError } from "@/lib/api";
import { DELIVERY_TYPE_DESC, DELIVERY_TYPE_LABEL, expressServiceDescription } from "@/lib/format";
import { informationUrl, SUPPORT_PHONE, SUPPORT_PHONE_RAW } from "@/lib/contact";
import { PARCEL_CATEGORIES } from "@/lib/parcelCategories";
import { formatVatRate, validPriceTax } from "@/lib/priceTax";

const API = `${API_ORIGIN}/api`;
const STORAGE_KEY = "dapnow_prefill_quote";

const DELIVERY_TYPES = [
  { id: "shared",   label: "Shared",   tagline: "In arrivo · driver privati",  icon: DELIVERY_TYPE_ICON.shared },
  { id: "standard", label: "Standard", tagline: "In giornata · flotta interna",     icon: DELIVERY_TYPE_ICON.standard },
  { id: "express",  label: "Express",  tagline: "Urgente",   icon: DELIVERY_TYPE_ICON.express },
  { id: "van",      label: "Van",      tagline: "Su richiesta · verifica preventiva", icon: DELIVERY_TYPE_ICON.van },
];
const PACKAGE_SIZES = PARCEL_CATEGORIES;
const TIME_SLOTS = [
  { id: "day",     label: "Giorno",  hint: "08–18" },
  { id: "evening", label: "Sera",    hint: "18–21" },
  { id: "night",   label: "Mattino presto", hint: "06:30–08" },
];

function fmtCHF(n) {
  if (n === null || n === undefined) return "—";
  return `CHF ${Number(n).toLocaleString("it-CH", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

// Phase 43.1 — euristica leggera per detection paese da stringa indirizzo
// (Google Places restituisce "..., Svizzera" / "..., Italia").
function detectCountry(addr) {
  if (!addr) return null;
  const s = String(addr).toLowerCase();
  if (s.includes("svizzera") || s.includes("switzerland") || /\bch\b/.test(s)) return "CH";
  if (s.includes("italia") || s.includes("italy")) return "IT";
  return null;
}

export default function PublicRateCalculator({ audience = "private" }) {
  const registrationAudience = audience === "business" ? "business" : "private";
  const isBusiness = registrationAudience === "business";
  const [pickup, setPickup] = useState("");
  const [delivery, setDelivery] = useState("");
  const [deliveryType, setDeliveryType] = useState("standard");
  const [packageSize, setPackageSize] = useState("medium");
  const [timeSlot, setTimeSlot] = useState("day");
  const [isWeekend, setIsWeekend] = useState(false);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const priceTax = validPriceTax(result?.price_tax) ? result.price_tax : null;
  // Express/Van remain request-only even against an API without review metadata.
  const reviewService = packageSize === "xl" ? "van" : deliveryType;
  const reviewLabel = DELIVERY_TYPE_LABEL[reviewService];
  const requiresManualReview = ["van", "express"].includes(reviewService) || result?.requires_manual_review === true;
  const parcel = PACKAGE_SIZES.find(item => item.id === packageSize);
  const reviewDescription = reviewService === "express"
    ? expressServiceDescription(result?.delivery_sla_minutes)
    : "Van su richiesta: il team deve verificare il collo, gli accessi e la movimentazione prima di prenotare o pagare.";
  const parcelLimits = reviewService === "van" ? "25 kg e 120 × 80 × 80 cm" : parcel?.specs;
  const manualRequestUrl = informationUrl(`Vorrei richiedere una verifica preventiva per una consegna ${reviewLabel}.\nRitiro: ${pickup.trim()}\nConsegna: ${delivery.trim()}\nLimiti per collo, imballaggio incluso: ${parcelLimits}.\n${reviewService === "express" ? `${reviewDescription}.\nOrario di ritiro richiesto:\n` : ""}Peso e dimensioni effettivi:\nAccessi, scale e necessità di movimentazione:\nAttendo la verifica e la conferma del team prima di prenotare o pagare.`);
  const requestVersion = useRef(0);
  const invalidateQuote = () => {
    requestVersion.current += 1;
    setResult(null);
    setError(null);
    setLoading(false);
    localStorage.removeItem(STORAGE_KEY);
  };
  const changeValue = (setter, value) => { invalidateQuote(); setter(value); };
  const chooseService = (service) => {
    const next = selectDeliveryService({ delivery_type: deliveryType, package_size: packageSize }, service);
    invalidateQuote();
    setDeliveryType(next.delivery_type);
    setPackageSize(next.package_size);
  };
  const chooseCategory = (size) => {
    const next = selectParcelCategory({ delivery_type: deliveryType, package_size: packageSize }, size);
    invalidateQuote();
    setDeliveryType(next.delivery_type);
    setPackageSize(next.package_size);
  };

  const submit = async (e) => {
    e.preventDefault();
    const p = pickup.trim();
    const d = delivery.trim();
    if (!p || !d) {
      setError("Completa tutti i dati dell'indirizzo per ottenere una stima precisa.");
      return;
    }
    // Phase 32 Batch A — basic structural sanity: serve almeno NPA + città (4+ digit + lettera)
    const hasZipCity = (s) => /\d{4}\s*[A-Za-zÀ-ÿ]/.test(s);
    if (!hasZipCity(p) || !hasZipCity(d)) {
      setError("Completa tutti i dati dell'indirizzo per ottenere una stima precisa. Serve almeno NPA e località (es. 6900 Lugano).");
      return;
    }
    setError(null);
    setLoading(true);
    const version = ++requestVersion.current;
    try {
      const { data } = await axios.post(`${API}/public/quote`, {
        pickup_address: pickup.trim(),
        delivery_address: delivery.trim(),
        delivery_type: deliveryType,
        package_size: packageSize,
        time_slot: timeSlot,
        is_weekend: isWeekend,
        ...(isBusiness ? { audience: "business" } : {}),
      });
      if (version !== requestVersion.current) return;
      if (isBusiness && (!validPriceTax(data.price_tax) || data.price_tax.audience !== "business"
          || Math.round(data.price_tax.total_chf * 100) !== Math.round(data.estimated_price * 100))) {
        setError("Il dettaglio IVA del preventivo aziendale non è disponibile. Riprova tra poco.");
        setResult(null);
        localStorage.removeItem(STORAGE_KEY);
        return;
      }
      setResult(data);
      // Only bookable services may become a signup/new-order prefill.
      if (["van", "express"].includes(deliveryType) || packageSize === "xl" || data.requires_manual_review === true) {
        localStorage.removeItem(STORAGE_KEY);
      } else localStorage.setItem(STORAGE_KEY, JSON.stringify({
        pickup_address: pickup.trim(),
        delivery_address: delivery.trim(),
        delivery_type: deliveryType,
        package_size: packageSize,
        time_slot: timeSlot,
        is_weekend: isWeekend,
        estimated_price: data.estimated_price,
        distance_km: data.distance_km,
        saved_at: new Date().toISOString(),
      }));
    } catch (e2) {
      if (version !== requestVersion.current) return;
      setError(formatApiError(e2.response?.data?.detail) || "Impossibile calcolare la stima ora.");
      setResult(null);
    } finally {
      if (version === requestVersion.current) setLoading(false);
    }
  };

  const trackConversion = async (event) => {
    try {
      await axios.post(`${API}/public/analytics`, {
        event,
        estimated_price: result?.estimated_price,
        delivery_type: deliveryType,
        package_size: packageSize,
        distance_km: result?.distance_km,
      });
    } catch { /* silent */ }
  };

  const reset = () => { setResult(null); };

  return (
    <section
      className="relative bg-white border-t border-zinc-100 break-words"
      data-testid="public-rate-calculator"
    >
      <div className="mx-auto max-w-6xl px-4 sm:px-6 py-16 sm:py-20">
        <div className="text-center max-w-2xl mx-auto">
          <span className="inline-flex items-center gap-1.5 rounded-full bg-dapnow-blue/10 text-dapnow-blue px-3 py-1 text-xs font-bold tracking-wider uppercase">
            <Sparkles className="size-3" /> Stima immediata · senza login
          </span>
          <h2 className="mt-4 font-display text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tighter">
            Calcola subito il costo della tua consegna
          </h2>
          <p className="mt-3 text-dapnow-muted text-base sm:text-lg">
            Inserisci gli indirizzi e scegli servizio e categoria del pacco.
          </p>
          <p className="mt-2 text-xs text-dapnow-muted font-medium" data-testid="rate-area-note">
            Area servita: Ticino e Grigioni italiano. Tutti i giorni, 06:30–21:00 (ora svizzera).
          </p>
          <p className="mt-2 text-sm text-dapnow-muted" data-testid="rate-tax-note">
            {isBusiness ? "Prezzi aziendali IVA esclusa. Nel preventivo trovi anche l’IVA e il totale da pagare."
              : "Prezzi per privati IVA inclusa."}
          </p>
        </div>

        <div className="mt-10 grid lg:grid-cols-5 gap-6 items-start">
          {/* Form */}
          <form
            onSubmit={submit}
            className="min-w-0 lg:col-span-3 rounded-3xl border border-zinc-200 bg-white shadow-[0_30px_80px_-30px_rgba(0,82,255,0.18)] p-5 sm:p-7 space-y-5"
            data-testid="rate-calc-form"
          >
            <div className="grid sm:grid-cols-2 gap-3">
              <div>
                <label htmlFor="rate-pickup" className="block text-[11px] uppercase tracking-wider font-bold text-dapnow-muted mb-1.5">
                  Ritiro
                </label>
                <div className="relative">
                  <MapPin className="absolute left-3 top-4 size-4 text-dapnow-blue z-10 pointer-events-none" />
                  <AddressAutocompleteInput
                    publicMode
                    id="rate-pickup"
                    aria-label="Ritiro"
                    value={pickup}
                    onChange={(e) => changeValue(setPickup, e.target.value)}
                    onPlaceSelected={(p) => changeValue(setPickup, p.address)}
                    placeholder="Es. Via Nassa, Lugano"
                    dataTestid="rate-pickup"
                    className="w-full h-12 rounded-2xl border border-zinc-200 pl-10 pr-3 text-sm font-medium focus:border-dapnow-blue focus:ring-4 focus:ring-dapnow-blue/15 focus:outline-none transition"
                  />
                </div>
              </div>
              <div>
                <label htmlFor="rate-delivery" className="block text-[11px] uppercase tracking-wider font-bold text-dapnow-muted mb-1.5">
                  Consegna
                </label>
                <div className="relative">
                  <MapPin className="absolute left-3 top-4 size-4 text-dapnow-red z-10 pointer-events-none" />
                  <AddressAutocompleteInput
                    publicMode
                    id="rate-delivery"
                    aria-label="Consegna"
                    value={delivery}
                    onChange={(e) => changeValue(setDelivery, e.target.value)}
                    onPlaceSelected={(p) => changeValue(setDelivery, p.address)}
                    placeholder="Es. Piazza Riforma, Bellinzona"
                    dataTestid="rate-delivery"
                    className="w-full h-12 rounded-2xl border border-zinc-200 pl-10 pr-3 text-sm font-medium focus:border-dapnow-blue focus:ring-4 focus:ring-dapnow-blue/15 focus:outline-none transition"
                  />
                </div>
              </div>
            </div>

            <div>
              <label className="block text-[11px] uppercase tracking-wider font-bold text-dapnow-muted mb-1.5">
                Tipo servizio
              </label>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2" data-testid="rate-delivery-type">
                {DELIVERY_TYPES.map((d) => {
                  const Icon = d.icon;
                  const active = deliveryType === d.id;
                  return (
                    <button
                      type="button"
                      key={d.id}
                      onClick={() => chooseService(d.id)}
                      disabled={d.id === "shared"}
                      aria-pressed={active}
                      data-testid={`rate-dt-${d.id}`}
                      className={`min-w-0 rounded-2xl border px-3 py-3 text-left transition disabled:bg-zinc-50 disabled:cursor-not-allowed ${active ? "border-dapnow-blue bg-dapnow-blue/5 ring-2 ring-dapnow-blue/15" : "border-zinc-200 hover:border-zinc-300"}`}
                    >
                      <Icon className={`size-4 mb-1.5 ${active ? "text-dapnow-blue" : "text-dapnow-muted"}`} />
                      <p className="text-sm font-bold tracking-tight text-dapnow-ink">{d.label}</p>
                      <p className="text-xs text-dapnow-muted mt-0.5">{d.tagline}</p>
                    </button>
                  );
                })}
              </div>
              <div aria-live="polite" aria-atomic="true" className="mt-3 rounded-xl bg-dapnow-bg p-3" data-testid="rate-selected-service-details">
                <p className="text-sm text-dapnow-ink" data-testid="rate-delivery-timing">{deliveryType === "express" ? expressServiceDescription(result?.delivery_sla_minutes) : DELIVERY_TYPE_DESC[deliveryType]}</p>
                {deliveryType === "standard" && timeSlot === "evening" && <p className="mt-2 text-xs text-dapnow-muted" data-testid="rate-evening-note">Per ritiri tra le 18:00 e le 21:00, <a className="text-dapnow-blue underline" href={informationUrl("Vorrei concordare un ritiro serale DapNow e il relativo orario di consegna.")}>contatta DapNow</a> per concordare i tempi prima di prenotare.</p>}
                {deliveryType === "express" && <p className="mt-2 text-xs text-dapnow-muted" data-testid="rate-express-selection-note">La conferma del team è necessaria prima di prenotare o pagare.</p>}
                {deliveryType === "van" && <p className="mt-2 text-xs text-dapnow-muted" data-testid="rate-parcel-service-note">Van è riservato ai colli XL. Il team verifica accessi e movimentazione prima di confermare prenotazione e pagamento.</p>}
              </div>
              <p className="mt-3 text-xs text-dapnow-muted">
                Shared in arrivo. <Link to="/become-driver" className="font-semibold text-dapnow-blue underline">Informazioni per driver privati</Link>.
              </p>
            </div>

            <div className="grid sm:grid-cols-2 gap-3">
              <div>
                <label className="block text-[11px] uppercase tracking-wider font-bold text-dapnow-muted mb-1.5">
                  <Package className="inline size-3 mr-1" /> Categoria del pacco
                </label>
                <div className="grid grid-cols-2 gap-2" data-testid="rate-package-size">
                  {PACKAGE_SIZES.map((s) => {
                    const active = packageSize === s.id;
                    return (
                      <button
                        type="button"
                        key={s.id}
                        onClick={() => chooseCategory(s.id)}
                        aria-pressed={active}
                        data-testid={`rate-pkg-${s.id}`}
                        className={`min-w-0 rounded-xl border px-3 py-2.5 text-left transition ${active ? "border-dapnow-blue bg-dapnow-blue/5 ring-2 ring-dapnow-blue/15" : "border-zinc-200 hover:border-zinc-300"}`}
                      >
                        <p className="text-sm font-bold text-dapnow-ink">{s.label}</p>
                        <p className="text-xs text-dapnow-muted">{s.specs}</p>
                      </button>
                    );
                  })}
                </div>
                <p className="mt-2 text-xs text-dapnow-muted">Limiti per singolo collo, imballaggio incluso.{packageSize === "small" && " Small è pensato per uno zaino compatibile."} Per colli oltre categoria, scale o movimentazioni particolari, richiedi prima una verifica.</p>
              </div>

              <div>
                <label className="block text-[11px] uppercase tracking-wider font-bold text-dapnow-muted mb-1.5">
                  <Clock className="inline size-3 mr-1" /> Fascia di ritiro
                </label>
                <div className="grid grid-cols-3 gap-2" data-testid="rate-time-slot">
                  {TIME_SLOTS.map((t) => {
                    const active = timeSlot === t.id;
                    return (
                      <button
                        type="button"
                        key={t.id}
                        onClick={() => changeValue(setTimeSlot, t.id)}
                        aria-pressed={active}
                        data-testid={`rate-ts-${t.id}`}
                        className={`min-w-0 rounded-xl border px-2 py-2.5 text-center transition ${active ? "border-dapnow-blue bg-dapnow-blue/5 ring-2 ring-dapnow-blue/15" : "border-zinc-200 hover:border-zinc-300"}`}
                      >
                        <p className="text-sm font-bold text-dapnow-ink">{t.label}</p>
                        <p className="text-xs text-dapnow-muted">{t.hint}</p>
                      </button>
                    );
                  })}
                </div>
                <p className="mt-2 text-xs text-dapnow-muted">La fascia indica quando richiedi il ritiro, non l’orario di consegna.</p>
                <label className="mt-2 inline-flex items-center gap-2 text-xs text-dapnow-muted cursor-pointer">
                  <input
                    type="checkbox"
                    checked={isWeekend}
                    onChange={(e) => changeValue(setIsWeekend, e.target.checked)}
                    data-testid="rate-weekend"
                    className="size-4 rounded border-zinc-300 text-dapnow-blue focus:ring-dapnow-blue"
                  />
                  Weekend / festivo (+30%)
                </label>
              </div>
            </div>

            {error && (
              <p className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-xl px-3 py-2" data-testid="rate-error">{error}</p>
            )}

            <button
              type="submit"
              disabled={loading}
              data-testid="rate-submit-btn"
              className="w-full h-13 inline-flex items-center justify-center gap-2 rounded-2xl bg-dapnow-ink text-white font-bold tracking-tight hover:bg-dapnow-blue transition py-4 text-base"
            >
              {loading ? (<><Loader2 className="size-4 animate-spin" /> Calcolo in corso…</>) :
               (<>Calcola stima <ArrowRight className="size-4" /></>)}
            </button>
          </form>

          {/* Result */}
          <div className="min-w-0 lg:col-span-2" data-testid="rate-result-area">
            {result ? (
              <div
                className="rounded-3xl bg-gradient-to-br from-dapnow-ink to-[#0b1220] text-white p-6 sm:p-7 shadow-[0_30px_80px_-30px_rgba(0,0,0,0.6)] sticky top-24"
                data-testid="rate-result-card"
              >
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <span className="inline-flex items-center gap-1.5 rounded-full bg-emerald-500/15 text-emerald-300 px-2.5 py-0.5 text-xs font-bold uppercase tracking-wider">
                    <Sparkles className="size-3" /> Stima immediata
                  </span>
                  <span className="text-xs uppercase tracking-wider font-semibold text-white/50">
                    Distanza stimata
                  </span>
                </div>

                <p className="overline text-white/60 mt-5">{isBusiness ? "Prezzo stimato · IVA esclusa" : "Prezzo stimato · IVA inclusa"}</p>
                <p className="font-display text-3xl sm:text-4xl xl:text-5xl font-extrabold tracking-tighter mt-1" data-testid="rate-estimated-price">
                  {fmtCHF(isBusiness ? priceTax?.net_chf : result.estimated_price)}
                </p>
                <p className="text-xs text-white/65 mt-2">
                  {result.distance_km.toFixed(1)} km · {result.delivery_type_name} · {result.package_size.toUpperCase()}
                </p>


                <CrossBorderNotice
                  pickupCountry={detectCountry(pickup)}
                  deliveryCountry={detectCountry(delivery)}
                  className="mt-4 bg-amber-500/10 border-amber-400/30 [&_p]:text-amber-100 [&_p.font-semibold]:text-amber-200 [&_svg]:text-amber-300"
                />

                <div className="mt-6 space-y-1 text-sm border-t border-white/10 pt-4">
                  <Row k="Base servizio" v={fmtCHF(result.base_price)} />
                  <Row k="Costo km" v={fmtCHF(result.distance_cost)} />
                  {result.package_multiplier > 1 && (
                    <Row k={`Maggiorazione pacco (×${result.package_multiplier.toFixed(2)})`} v="" />
                  )}
                  {result.time_slot_surcharge > 0 && (
                    <Row k={`Maggiorazione ${result.time_slot}`} v={`+ ${fmtCHF(result.time_slot_surcharge)}`} />
                  )}
                  {result.weekend_surcharge > 0 && (
                    <Row k="Weekend/festivo" v={`+ ${fmtCHF(result.weekend_surcharge)}`} />
                  )}
                  {(isBusiness ? priceTax?.net_chf : result.gross_price) <= result.minimum_price && (
                    <Row k="Tariffa minima applicata" v={fmtCHF(result.minimum_price)} muted />
                  )}
                  {isBusiness && priceTax && (
                    <div data-testid="rate-tax-breakdown">
                      <Row k="Imponibile stimato" v={fmtCHF(priceTax.net_chf)} />
                      <Row k={`IVA ${formatVatRate(priceTax.vat_rate)}`} v={fmtCHF(priceTax.vat_chf)} />
                    </div>
                  )}
                  <div className="grid grid-cols-[minmax(0,1fr)_auto] gap-x-3 text-base font-bold pt-3 mt-2 border-t border-white/15">
                    <span>Totale stimato IVA inclusa</span>
                    <span>{fmtCHF(result.estimated_price)}</span>
                  </div>
                </div>

                <div className="mt-6 grid gap-2">
                  {requiresManualReview ? (
                    <>
                      <p className="text-sm text-amber-100" data-testid={`rate-${reviewService}-review-note`}>
                        {typeof result.manual_review_message === "string" ? result.manual_review_message : reviewDescription}
                      </p>
                      <p className="text-xs text-white/75">{parcel?.specs} per collo, imballaggio incluso.</p>
                      {reviewService === "express" && <p className="text-xs text-white/75">La conferma deve prevedere la consegna entro le 21:00; la stima non estende l’orario di servizio.</p>}
                      <a href={manualRequestUrl} data-testid={`rate-cta-${reviewService}-review`} className="min-h-12 px-4 py-3 inline-flex items-center justify-center rounded-2xl bg-dapnow-blue text-white text-center font-bold hover:bg-dapnow-blue/90 transition">
                        Richiedi verifica {reviewLabel}
                      </a>
                      <a href={`tel:+${SUPPORT_PHONE_RAW}`} className="text-sm text-center underline" data-testid={`rate-${reviewService}-phone`}>Chiama DapNow: {SUPPORT_PHONE}</a>
                      <p className="text-xs text-white/75">Si apre una bozza email da inviare. La richiesta non è una prenotazione né un’approvazione; questa stima non crea ordini o addebiti.</p>
                    </>
                  ) : (
                    <>
                  <Link
                    to={`/register?audience=${registrationAudience}&prefill=quote`}
                    onClick={() => trackConversion("public_quote_converted_to_signup")}
                    data-testid="rate-cta-book"
                    className="min-h-12 px-3 py-3 inline-flex items-center justify-center gap-2 rounded-2xl bg-dapnow-blue text-white text-center font-bold hover:bg-dapnow-blue/90 transition"
                  >
                    Prenota questa consegna <ArrowRight className="size-4" />
                  </Link>
                  <Link
                    to={`/register?audience=${registrationAudience}`}
                    onClick={() => trackConversion("public_quote_converted_to_signup")}
                    data-testid="rate-cta-register"
                    className="h-11 inline-flex items-center justify-center rounded-2xl border border-white/15 text-white/90 hover:bg-white/10 transition text-sm font-semibold"
                  >
                    Registrati per continuare
                  </Link>
                    </>
                  )}
                </div>

                <p className="mt-5 text-[11px] text-white/50 leading-relaxed" data-testid="rate-disclaimer">
                  {requiresManualReview ? "Importo indicativo soggetto alla verifica preventiva del team DapNow." : result.disclaimer}
                </p>

                <button
                  type="button"
                  onClick={reset}
                  data-testid="rate-reset-btn"
                  className="mt-3 text-xs font-semibold text-white/60 hover:text-white"
                >
                  ← Modifica i dati
                </button>
              </div>
            ) : (
              <div className="rounded-3xl border border-dashed border-zinc-300 bg-zinc-50/60 p-7 text-center" data-testid="rate-empty">
                <Truck className="size-9 mx-auto text-dapnow-muted mb-3" />
                <p className="text-sm text-dapnow-muted">
                  La tua stima apparirà qui in pochi secondi.
                </p>
                <p className="text-xs text-dapnow-muted mt-2">
                  Nessun login richiesto · prezzo trasparente in CHF
                </p>
              </div>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}

function Row({ k, v, muted = false }) {
  return (
    <div className={`grid grid-cols-[minmax(0,1fr)_auto] gap-x-3 py-1.5 ${muted ? "text-white/55" : "text-white/85"}`}>
      <span className="text-xs">{k}</span>
      <span className="text-xs font-semibold text-right">{v}</span>
    </div>
  );
}
